// @ts-nocheck
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Calendar,
  MapPin,
  ArrowRight,
  CheckCircle,
  Trophy,
  Star,
  Award,
  TrendingUp,
  Eye,
  Clock,
  Globe,
  Zap,
  Shield,
  Heart,
  Video,
  Users,
  Target,
  Filter,
  Search,
  GraduationCap,
  Ruler,
  ExternalLink
} from "lucide-react";

export default function Commitments() {
  const [selectedSport, setSelectedSport] = useState("All Sports");
  const [selectedDivision, setSelectedDivision] = useState("All Divisions");
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommitments();
  }, []);

  const fetchCommitments = async () => {
    try {
      const { data, error } = await supabase
        .from('commitments')
        .select('*')
        .eq('is_active', true)
        .order('commitment_date', { ascending: false });

      if (error) throw error;
      
      // Transform database data to match component structure
      const transformedData = data?.map(commitment => ({
        id: commitment.id,
        athleteName: commitment.athlete_name,
        sport: commitment.sport,
        position: commitment.position || "Position",
        highSchool: "High School", // Not in DB
        city: "City", // Not in DB
        state: "State", // Not in DB
        collegeName: commitment.school_name,
        collegeState: "State", // Not in DB
        division: commitment.division === "Unknown" ? "D1" : commitment.division,
        commitmentDate: commitment.commitment_date,
        height: "6'0\"", // Not in DB
        weight: "180 lbs", // Not in DB
        gpa: "3.8", // Not in DB
        highlights: [], // Not in DB
        profileImage: commitment.athlete_image_url || "https://images.pexels.com/photos/5384617/pexels-photo-5384617.jpeg",
        scholarshipType: "Full Ride", // Not in DB
        recruitedBy: "RecruitLook Scout" // Not in DB
      })) || [];

      setCommitments(transformedData);
    } catch (error) {
      console.error('Error fetching commitments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic sports list from data
  const uniqueSports = ["All Sports", ...new Set(commitments.map(c => c.sport))];
  const sports = uniqueSports;
  const divisions = ["All Divisions", "D1", "D2", "D3", "NAIA", "NJCAA"];
  

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Filter commitments based on search criteria
  const filteredCommitments = commitments.filter(commitment => {
    return (
      (selectedSport === "All Sports" || commitment.sport === selectedSport) &&
      (selectedDivision === "All Divisions" || commitment.division === selectedDivision)
    );
  });


  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src="/lovable-uploads/4dae9141-f50d-4db7-910c-189a960de661.png"
            alt="Basketball hero background"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-secondary text-black px-6 py-3 text-sm font-black tracking-wider mb-8">
              COLLEGE COMMITMENTS
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
              DREAMS BECOME
              <span className="block text-secondary">REALITY</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Celebrating the latest college commitments from talented student-athletes 
              who have achieved their recruiting goals and secured their athletic futures.
            </p>
          </div>

        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-12 bg-gradient-to-br from-gray-100 to-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">SPORT</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent font-semibold"
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
              >
                {sports.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">DIVISION</label>
              <select
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent font-semibold"
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
              >
                {divisions.map(division => (
                  <option key={division} value={division}>{division}</option>
                ))}
              </select>
            </div>


            <div className="flex items-end">
              <Button 
                className="w-full bg-slate-900 text-white hover:bg-slate-800 font-black py-3"
                onClick={() => {
                  setSelectedSport("All Sports");
                  setSelectedDivision("All Divisions");
                }}
              >
                CLEAR FILTERS
              </Button>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-6 text-center">
            <Badge className="bg-secondary text-black px-4 py-2 font-black">
              {filteredCommitments.length} COMMITMENTS FOUND
            </Badge>
          </div>
        </div>
      </section>


      {/* Commitments Grid */}
      <section className="py-24 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredCommitments.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-black text-gray-600 mb-2">No Commitments Found</h3>
              <p className="text-gray-500">Try adjusting your search criteria or filters.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCommitments.map((commitment) => (
                <div key={commitment.id} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300 group">
                  {/* Athlete Image */}
                  <div className="relative h-[500px] overflow-hidden">
                    <img 
                      src={commitment.profileImage}
                      alt={commitment.athleteName}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    
                    {/* Sport Badge */}
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-secondary text-black px-3 py-1 text-xs font-black">
                        {commitment.sport.toUpperCase()}
                      </Badge>
                    </div>

                    {/* Division Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-blue-600 text-white px-3 py-1 text-xs font-black">
                        {commitment.division}
                      </Badge>
                    </div>
                  </div>

                  {/* Commitment Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-black text-slate-900 mb-4">{commitment.athleteName}</h3>

                    {/* College Info */}
                    <div className="bg-slate-900 text-white p-4 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <GraduationCap className="w-5 h-5 text-secondary" />
                        <span className="font-black text-secondary">COMMITTED TO</span>
                      </div>
                      <h4 className="text-lg font-black">{commitment.collegeName}</h4>
                      <p className="text-gray-300 text-sm">{commitment.division}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </section>


      {/* Call to Action for Athletes */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-black mb-8 text-slate-900">
            YOUR COMMITMENT
            <span className="block text-secondary">STARTS HERE</span>
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Join thousands of student-athletes who have achieved their college dreams. 
            Start your recruiting journey today and see your name on our next commitment announcement.
          </p>
          
          <div className="flex justify-center mb-12">
            <Button 
              size="lg" 
              className="bg-secondary hover:bg-secondary/90 text-black text-xl px-12 py-6 font-black tracking-wide"
              onClick={() => window.location.href = '/login'}
            >
              SIGN IN
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </div>

          <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-secondary" />
              <span className="font-semibold">Free Profile Creation</span>
            </div>
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-secondary" />
              <span className="font-semibold">Expert Guidance</span>
            </div>
            <div className="flex items-center space-x-2">
              <Trophy className="w-5 h-5 text-secondary" />
              <span className="font-semibold">Proven Results</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
